import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import Banner from '@/components/Banner';
import { Button } from '@/components/Buttons';
import {
    zipResidentialUnitDocuments,
    ResidentialUnitDocumentSearchOptions,
    ProjectDocumentSearchOptions,
    zipProjectDocuments,
    getZippableResidentialUnitDocuments,
    getZippableProjectDocuments,
} from '@/services/documents';
import { HttpStatusCode } from '@/HttpStatusCodes';
import { ValidationSummary } from '@/components/forms/validation';
import { TranslatableText, ResidentialUnitDocument, ProjectDocument } from '@/types';

interface DocumentZipDownloadDialogProps {
    isOpen: boolean;
    toggle(): void;
    filters: ResidentialUnitDocumentSearchOptions | ProjectDocumentSearchOptions;
    isProjectDocuments?: boolean;
}

const DocumentZipCreateDialog: React.FC<DocumentZipDownloadDialogProps> = ({
    isOpen,
    toggle,
    filters,
    isProjectDocuments,
}) => {
    const [documents, setDocuments] = useState<ResidentialUnitDocument[] | ProjectDocument[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<TranslatableText[]>([]);

    const handleCreateZip = async () => {
        setIsLoading(true);
        setErrors([]);
        try {
            if (isProjectDocuments) {
                await zipProjectDocuments(filters as ProjectDocumentSearchOptions);
            } else {
                await zipResidentialUnitDocuments(filters);
            }
            Toast.info(<Trans>Zip is being created.</Trans>);
            toggle();
        } catch (e: any) {
            if (e.status === HttpStatusCode.BadRequest) {
                setErrors([...errors, <Trans>No documents to zip with this filter configuration.</Trans>]);
                Toast.warn(<Trans>No documents to zip with this filter configuration.</Trans>);
            }
        } finally {
            setIsLoading(false);
        }
    };
    const handleOpen = async () => {
        if (isProjectDocuments) {
            setDocuments(await getZippableProjectDocuments(filters as ProjectDocumentSearchOptions));
        } else {
            setDocuments(await getZippableResidentialUnitDocuments(filters));
        }
    };
    return (
        <Modal onOpen={handleOpen} isOpen={isOpen} toggle={toggle}>
            <ModalHeader>
                <Trans>Create a zip file</Trans>
            </ModalHeader>
            <ModalBody className="relative">
                <Trans>
                    You are about to create a zip file containing the <strong>{documents?.length}</strong> documents that are
                    currently filtered.
                </Trans>
                <Banner type="info">
                    <Trans>
                        <strong>Please note, </strong>
                        zipping multiple files can take a while. You will receive a notification when the zip file is ready
                        to be downloaded.
                    </Trans>
                </Banner>
                <ValidationSummary externalErrors={errors} />
            </ModalBody>
            <ModalFooter>
                <Button color="tertiary" onClick={toggle}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button loading={isLoading} color="primary" onClick={handleCreateZip}>
                    <Trans>Create zip</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default DocumentZipCreateDialog;
